import Factory from '@/Factories/Factory';
import * as interfaces from '#/CoreFrameInterfaces';
import { IDetailViewGroupDTO } from "coreframetypings/dto";
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import GroupFactoryTuple from '@/Factories/GroupFactoryTuple';
import ControlViewModel from '@/ViewModels/ControlViewModel';
import Control from '@/Controls/Control';
import { ITupleProvider } from '#/DefaultViewExtensionInterfaces';
import { IGroupFactoryConstructorParameter } from '#/CoreFrameInterfaces';
import AsyncTypeGuards from '@/Utility/AsyncTypeGuards';
import Context from '@/ViewModels/Context';

/**
* Repräsentiert die Oberklasse aller GroupFactories
*/
export default class GroupFactory extends Factory implements ITupleProvider<GroupFactoryTuple> {

    /** 
    * Beinhaltet die aktuelle DetailViewGroupDTO
    */
    public detailViewGroup: IDetailViewGroupDTO;

    /** 
     * Beinhaltet den Namen des verwendeten Layouts
     */
    public layout: string;

    /** 
     * Definiert die Breite der Factory im Layout
     */
    public colspan: string;

    /** 
     * Der Kontext der GroupFactory
     */
    public context: DetailViewContext;

    /** 
     * Gibt an, ob die Factory sichtbar ist
     */
    public isVisible: KnockoutObservable<boolean>;

    /** 
     * Gibt an, ob sich die GroupFactory in einer SummaryView befindet
     */
    public isSummaryViewMode: boolean;

    /**
     * Enthält das GroupFactoryTuple
     */
    public tuple: GroupFactoryTuple;

    /**
     * Gibt an, ob ein Vorfahre geschlossen ist
     */
    public ancestorIsCollapsed: KnockoutObservable<boolean>;

    /**
     * Enthält das Control
     */
    public control: Control;

    protected jqueryControl: JQuery<HTMLElement>;

    /**
     * Enthält das ViewModel
     */
    public viewModel: ControlViewModel;

    constructor(params: interfaces.IGroupFactoryConstructorParameter) {
        super(params);
        this.isGroupFactory = true;

        this.detailViewGroup = this.paramOrDefault(params.detailViewGroup, null);
        this.layout = this.paramOrDefault(params.layout, 'NoneLayout');
        this.colspan = this.paramOrDefault(params.colspan, 'One');
        this.context = this.paramOrDefault(params.context, null);
        this.isVisible = this.observableOrDefault(params.isVisible, true);
        this.isSummaryViewMode = this.paramOrDefault(params.isSummaryViewMode, false);
        this.ancestorIsCollapsed = this.observableOrDefault(params.ancestorIsCollapsed, false);
        this.isDisposable = true;
        this.undisposeProperties.push('context');
    }

    public static async createInstanceAsync<T extends IGroupFactoryConstructorParameter>(
        group: IDetailViewGroupDTO,
        context: Context,
        additionalOptions: T
    ): Promise<{ groupFactory?: GroupFactory, tuple?: GroupFactoryTuple }> {
        if (context?.isDisposed !== false)
            return {};

        const groupFactory = Factory.createInstance(group.uiGenerator, {
            detailViewGroup: group,
            context,
            ...additionalOptions
        }, 'DummyControl') as GroupFactory;

        const tuple = AsyncTypeGuards.isAsyncTupleProvider<GroupFactoryTuple>(groupFactory)
            ? await groupFactory.getTupleAsync()
            : groupFactory.getTuple();

        if (!context.isDisposed) {
            context.setCreatedTuple(group.name, tuple);
            context.setCreatedFactory(groupFactory);
        }

        return { groupFactory, tuple };
    }

    /**
    * Erzeugt das FactoryTuple und die Factory
    */
    public getTuple(): GroupFactoryTuple {
        if (this.tuple)
            return this.tuple;

        const $control = this.getControl();
        this.tuple = new GroupFactoryTuple(this, $control);
        return this.tuple;
    }

    /**
     * Gibt das JQuery-Element zurück
     */
    public getControl(): JQuery<HTMLElement> {
        if (this.jqueryControl != null)
            return this.jqueryControl;

        this.viewModel = this.createViewModel();
        this.control = this.createControl();
        const tuple = this.control.getTuple();

        return tuple.control;
    }

    /**
     * Erzeugt das ViewModel
     */
    protected createViewModel(): ControlViewModel {
        return null;
    }

    /**
     * Erzeugt das Control
     */
    protected createControl(): Control {
        return null;
    }

    /**
     * Returns the group ergname (with nested context path, dot-separated)
     * @param group
     */
    protected getFullGroupErgName(group: IDetailViewGroupDTO = this.detailViewGroup): string {
        return this.concatWithNestedContextPathIfNecessary(group.ergName);
    }

    /**
     * Returns the group name (with nested context path, dot-separated)
     * @param group
     */
    protected getFullGroupName(group: IDetailViewGroupDTO = this.detailViewGroup): string {
        return this.concatWithNestedContextPathIfNecessary(group.name);        
    }

    private concatWithNestedContextPathIfNecessary(prop: string): string {
        let paths = [prop];

        if (this.context instanceof DetailViewContext && this.context.isNestedDetailViewContext())
            paths.push(this.context.getNestedContextPath());

        return paths.join('.');
    }
}